import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  ArrowLeft, Share2, Check, MapPin, Award, Truck,
  TrendingUp, ChevronRight, Loader2, AlertCircle,
} from "lucide-react";
import { api } from "@/lib/api";

const DARK = "#0A0A0A";
const ACCENT = "#FF4D00";
const GOLD = "#F5C842";
const CARD = "#111111";
const TEAL = "#3ECFB2";
const API = process.env.REACT_APP_BACKEND_URL;

function DocBadge({ label, ok }) {
  return (
    <span
      className="font-head text-[9px] uppercase tracking-[0.12em] px-1.5 py-0.5"
      style={{
        background: ok ? "rgba(74,222,128,0.12)" : "rgba(255,255,255,0.04)",
        color: ok ? "#4ADE80" : "rgba(255,255,255,0.2)",
        border: `1px solid ${ok ? "rgba(74,222,128,0.3)" : "rgba(255,255,255,0.06)"}`,
      }}
    >
      {label}
    </span>
  );
}

export default function DriverPublicProfile() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [driver, setDriver] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!slug) return;
    api.marketGetPublicProfile(slug)
      .then(setDriver)
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  function copyLink() {
    const canonicalUrl = `https://hylascompanion.com/driver/${driver.slug}`;
    navigator.clipboard.writeText(canonicalUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  const photoUrl = driver?.has_photo
    ? `${API}/api/market/drivers/${driver.id}/photo`
    : null;

  const initials = driver
    ? `${(driver.name.split(" ")[0] || "?")[0]}${(driver.name.split(" ")[1] || "?")[0]}`.toUpperCase()
    : "??";

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: DARK }}>
        <Loader2 className="w-8 h-8 animate-spin text-white/20" />
      </div>
    );
  }

  if (notFound || !driver) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-6 px-6" style={{ background: DARK }}>
        <AlertCircle className="w-12 h-12 text-white/20" />
        <div className="text-center">
          <p className="font-head text-xl font-bold uppercase tracking-[0.1em] text-white">Driver Not Found</p>
          <p className="text-white/40 text-sm mt-2">This profile may have been removed or the link is incorrect.</p>
        </div>
        <Link
          to="/market"
          className="flex items-center gap-2 font-head text-sm uppercase tracking-[0.2em] px-6 py-3 transition-all hover:brightness-110"
          style={{ background: ACCENT, color: "white" }}
        >
          View All Drivers
        </Link>
      </div>
    );
  }

  const canonicalUrl = `https://hylascompanion.com/driver/${driver.slug}`;
  const title = `${driver.name} — ${driver.cdl_class} Driver · ${driver.state} | Hylas Companion Market`;
  const description = `${driver.name} is a ${driver.cdl_class} truck driver based in ${driver.state}${driver.position ? ` specializing in ${driver.position}` : ""}. View their profile and place a bid on the Hylas Companion Market.`;

  // Always use the static brand banner for OG image (consistent across all driver share cards)
  const ogImage = "https://hylascompanion.com/og-banner.jpg";

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonicalUrl} />

        {/* Open Graph — primary */}
        <meta property="og:site_name" content="Hylas Companion Market" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:type" content="profile" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={ogImage} />
        <meta property="og:image:secure_url" content={ogImage} />
        <meta property="og:image:width" content="1264" />
        <meta property="og:image:height" content="848" />
        <meta property="og:image:type" content="image/jpeg" />
        <meta property="og:image:alt" content="Hylas Companion CDL Driver Marketplace" />

        {/* Twitter / X */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={`${driver.name} · ${driver.cdl_class} · ${driver.state}`} />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={ogImage} />

        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Person",
          "name": driver.name,
          "jobTitle": `${driver.cdl_class} Commercial Truck Driver`,
          "address": { "@type": "PostalAddress", "addressRegion": driver.state },
          "url": canonicalUrl,
        })}</script>
      </Helmet>

      <div className="min-h-screen pt-20" style={{ background: DARK }}>
        {/* Back link — sits just below the fixed Navbar */}
        <div className="px-4 sm:px-8 py-3" style={{ borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
          <Link
            to="/market"
            className="inline-flex items-center gap-2 font-head text-[10px] uppercase tracking-[0.2em] text-white/35 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Hylas Market
          </Link>
        </div>

        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-10 sm:py-16">
          {/* Profile hero */}
          <div
            className="relative overflow-hidden mb-6"
            style={{ background: CARD, border: "1px solid rgba(255,255,255,0.08)" }}
          >
            {/* Gold accent top */}
            <div style={{ height: 3, background: `linear-gradient(90deg, ${ACCENT}, ${GOLD})` }} />

            <div className="flex flex-col sm:flex-row items-start gap-6 p-6 sm:p-8">
              {/* Photo / initials */}
              <div
                className="shrink-0 flex items-center justify-center overflow-hidden"
                style={{
                  width: 120, height: 140,
                  background: photoUrl ? "#111" : `linear-gradient(135deg, ${ACCENT}30, ${GOLD}20)`,
                  border: `2px solid ${photoUrl ? "rgba(255,255,255,0.1)" : ACCENT + "40"}`,
                }}
              >
                {photoUrl ? (
                  <img
                    src={photoUrl}
                    alt={driver.name}
                    className="w-full h-full object-cover object-top"
                  />
                ) : (
                  <span
                    className="font-head text-4xl font-black"
                    style={{ color: ACCENT }}
                  >
                    {initials}
                  </span>
                )}
              </div>

              {/* Info */}
              <div className="flex-1 min-w-0">
                <span
                  className="inline-block font-head text-[9px] uppercase tracking-[0.3em] px-2 py-0.5 mb-3"
                  style={{ background: `${TEAL}20`, color: TEAL, border: `1px solid ${TEAL}40` }}
                >
                  {driver.market_status === "approved" ? "Available" : driver.market_status}
                </span>

                <h1 className="font-head text-3xl sm:text-4xl font-black uppercase tracking-[0.04em] text-white leading-none mb-2">
                  {driver.name}
                </h1>

                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mb-4">
                  {driver.state && (
                    <div className="flex items-center gap-1.5 text-white/50 text-sm">
                      <MapPin className="w-3.5 h-3.5 shrink-0" />
                      {driver.state}
                    </div>
                  )}
                  {driver.cdl_class && (
                    <div className="flex items-center gap-1.5 text-sm" style={{ color: GOLD }}>
                      <Award className="w-3.5 h-3.5 shrink-0" />
                      {driver.cdl_class}
                    </div>
                  )}
                  {driver.position && (
                    <div className="flex items-center gap-1.5 text-white/40 text-sm">
                      <Truck className="w-3.5 h-3.5 shrink-0" />
                      {driver.position}
                    </div>
                  )}
                </div>

                <div className="flex flex-wrap gap-1.5">
                  <DocBadge label="CDL" ok={driver.has_cdl} />
                  <DocBadge label="MED" ok={driver.has_medical} />
                  <DocBadge label="MVR" ok={driver.has_mvr} />
                  <DocBadge label="PSP" ok={driver.has_psp} />
                  <DocBadge label="CLR" ok={driver.has_clearinghouse} />
                </div>
              </div>
            </div>

            {/* Bid stats row */}
            <div
              className="px-6 sm:px-8 py-4 grid grid-cols-3 gap-4"
              style={{ borderTop: "1px solid rgba(255,255,255,0.06)", background: "rgba(255,255,255,0.02)" }}
            >
              <div>
                <p className="font-head text-[9px] uppercase tracking-[0.2em] text-white/30 mb-0.5">Active Bids</p>
                <p className="font-head text-2xl font-black" style={{ color: driver.bid_count > 0 ? ACCENT : "rgba(255,255,255,0.15)" }}>
                  {driver.bid_count}
                </p>
              </div>
              <div>
                <p className="font-head text-[9px] uppercase tracking-[0.2em] text-white/30 mb-0.5">Top Bid</p>
                <p className="font-head text-2xl font-black" style={{ color: driver.top_bid ? GOLD : "rgba(255,255,255,0.15)" }}>
                  {driver.top_bid ? `$${driver.top_bid.toLocaleString()}` : "—"}
                </p>
              </div>
              <div>
                <p className="font-head text-[9px] uppercase tracking-[0.2em] text-white/30 mb-0.5">Days on Market</p>
                <p className="font-head text-2xl font-black" style={{ color: TEAL }}>
                  {driver.created_at
                    ? Math.min(12, Math.max(1, Math.floor((Date.now() - new Date(driver.created_at).getTime()) / 86400000)))
                    : "—"}
                </p>
              </div>
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              data-testid="public-profile-bid-cta"
              onClick={() => navigate(`/market?bid=${encodeURIComponent(driver.slug)}&open_carrier=1`)}
              className="flex-1 flex items-center justify-center gap-2 font-head text-sm font-black uppercase tracking-[0.2em] py-4 transition-all hover:brightness-110"
              style={{ background: ACCENT, color: "white" }}
            >
              <TrendingUp className="w-4 h-4" />
              Sign Up as Carrier &amp; Bid
              <ChevronRight className="w-4 h-4 ml-auto" />
            </button>

            <button
              data-testid="public-profile-copy-link"
              onClick={copyLink}
              className="flex items-center justify-center gap-2 font-head text-sm uppercase tracking-[0.2em] py-4 px-6 border transition-all"
              style={{
                borderColor: copied ? "rgba(74,222,128,0.5)" : "rgba(255,255,255,0.12)",
                color: copied ? "#4ADE80" : "rgba(255,255,255,0.5)",
                background: copied ? "rgba(74,222,128,0.06)" : "transparent",
              }}
            >
              {copied ? <Check className="w-4 h-4" /> : <Share2 className="w-4 h-4" />}
              {copied ? "Link Copied" : "Share Profile"}
            </button>
          </div>

          {/* Legal note */}
          <p className="mt-8 text-center text-[10px] text-white/15 leading-relaxed">
            This driver profile is published on the Hylas Companion Market with the driver's consent.
            All bids and placements are subject to{" "}
            <Link to="/market/terms" className="underline hover:text-white/30 transition-colors">
              Hylas Companion Marketplace Terms
            </Link>.
          </p>
        </div>
      </div>
    </>
  );
}
